import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Monitor, Plus, KeyRound, Copy } from "lucide-react";
import { theme, card, btnPrimary, type as text, pageHeading, pageSubtitle } from "../theme";
import { api, apiErrorMessage } from "../api/client";
import { useCurrentUser } from "../auth/useCurrentUser";
import { LoadingBlock } from "../components/Spinner";
import { SheetCard, StatusTag } from "../components/Drawing";
import { SummaryTiles } from "../components/RegisterToolbar";
import { EmptyState, Field, ModalHeader, ModalOverlay, fmtDateTime, input, secondaryBtn, tableHeadRow, textarea, relativeDue } from "./resilience/shared";

// Devices — PCs running the Abacas WingGuard app that report their Cyber Essentials findings here
// (backend routes/devices.routes.ts, docs/DEVICE-LINK.md). Reading is open to every member; adding a
// device, setting the support key and asking a PC to do something need integrations:admin; opening a
// support password needs org:admin and a reason.

export interface DeviceRow {
  id: string; deviceId: string; name: string; os: string | null; formFactor: string | null; status: "enrolled" | "revoked";
  enrolledAt: string; lastSeenAt: string | null; lastCheckedAt: string | null;
  passCount: number; failCount: number; unknownCount: number; declaredCount: number;
}
interface Code { id: string; expiresAt: string; usesLeft: number; createdAt: string }
interface TrustedKey { keyId: string; modulus: string; exponent: string; retired: boolean }
interface Escrow {
  id: string; createdUtc: string; expiresUtc: string; receivedAt: string; expired: boolean;
  linkedDevice: { id: string; name: string };
  accesses: Array<{ id: string; action: string; reason: string; at: string }>;
}
interface View { devices: DeviceRow[]; escrowKeySet: boolean; escrowKeySetAt: string | null; codes: Code[]; keys: TrustedKey[] }

const th: React.CSSProperties = { padding: "10px 14px", borderBottom: "1px solid var(--c-line)", fontWeight: 600 };
const td: React.CSSProperties = { padding: "10px 14px", color: theme.color.muted, verticalAlign: "top" };
const errorText: React.CSSProperties = { ...text.hint, color: theme.color.dangerText, marginTop: 10 };
const STALE_DAYS = 7;

export const isStale = (d: Pick<DeviceRow, "lastSeenAt">) => !d.lastSeenAt || Date.now() - new Date(d.lastSeenAt).getTime() > STALE_DAYS * 86_400_000;

export function DevicesPage() {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const canManage = !!user?.permissions?.includes("integrations:admin");
  const isAdmin = !!user?.permissions?.includes("org:admin");
  const [data, setData] = useState<View | null>(null);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [showRemoved, setShowRemoved] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.get<View>("/devices")); setError(""); }
    catch (e) { setError(apiErrorMessage(e, "Could not load the devices.")); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const live = useMemo(() => (data?.devices ?? []).filter((d) => d.status === "enrolled"), [data]);
  const removed = useMemo(() => (data?.devices ?? []).filter((d) => d.status === "revoked"), [data]);
  const shown = showRemoved ? [...live, ...removed] : live;

  return (
    <div className="page-pad">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap", marginBottom: 20 }}>
        <div>
          <h1 style={{ ...pageHeading, display: "flex", alignItems: "center", gap: 8 }}><Monitor size={20} /> Devices</h1>
          <p style={{ ...pageSubtitle, maxWidth: 780 }}>
            PCs running the Abacas WingGuard app. Each one checks itself against Cyber Essentials and reports here, so you can see which have gaps and
            which answers are only a person&apos;s word. The findings are kept as evidence for your Cyber Essentials answers.
          </p>
        </div>
        {canManage && data && (
          <button type="button" style={{ ...btnPrimary, display: "flex", alignItems: "center", gap: 6 }} onClick={() => setAdding(true)}>
            <Plus size={14} /> Add a device
          </button>
        )}
      </div>

      {error && <div style={{ ...errorText, marginTop: 0, marginBottom: 12 }}>{error}</div>}
      {!data ? (error ? null : <LoadingBlock />) : (
        <div style={{ display: "grid", gap: 20 }}>
          <SummaryTiles tiles={[
            { key: "all", label: "Devices", value: live.length },
            { key: "gaps", label: "With gaps", value: live.filter((d) => d.failCount > 0).length, tone: live.some((d) => d.failCount > 0) ? "warning" : "neutral", hint: "at least one check failing" },
            { key: "declared", label: "Resting on a declaration", value: live.filter((d) => d.declaredCount > 0).length, hint: "some answers are a person's word, not checked" },
            { key: "stale", label: `Silent for ${STALE_DAYS} days`, value: live.filter(isStale).length, tone: live.some(isStale) ? "warning" : "neutral", hint: "may be off, or no longer reporting" },
          ]} />

          <div style={{ ...card, padding: 0, overflowX: "auto" }}>
            {shown.length === 0 ? (
              <div style={{ padding: 18 }}>
                <EmptyState
                  title="No devices yet"
                  detail={canManage ? "Choose Add a device to get a code, then enter it in the WingGuard app on the PC." : "Someone who manages connections can add a device."}
                />
              </div>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: text.body.fontSize }}>
                <thead><tr style={tableHeadRow}>
                  <th style={th}>Device</th><th style={th}>Checks</th><th style={th}>Gaps</th><th style={th}>Evidence</th><th style={th}>Last heard from</th>
                </tr></thead>
                <tbody>
                  {shown.map((d) => {
                    const total = d.passCount + d.failCount + d.unknownCount;
                    return (
                      <tr key={d.id} onClick={() => navigate(`/devices/${d.id}`)} style={{ cursor: "pointer", borderBottom: "1px solid var(--c-border)", opacity: d.status === "revoked" ? 0.6 : 1 }}>
                        <td style={{ ...td, color: theme.color.text, fontWeight: 600 }}>{d.name}{d.os && <div style={{ ...text.hint, fontWeight: 400 }}>{d.os}</div>}{d.status === "revoked" && <div style={text.hint}>Removed</div>}</td>
                        <td style={td}>{total ? `${d.passCount} of ${total} pass` : "Nothing received yet"}</td>
                        <td style={td}>{d.failCount > 0 ? <StatusTag tone="gap">{d.failCount} failing</StatusTag> : total ? <StatusTag tone="met">None</StatusTag> : <StatusTag tone="none">Not known</StatusTag>}</td>
                        <td style={td}>{d.declaredCount > 0 ? <StatusTag tone="partial" title="Passed because a person said so">{d.declaredCount} declared</StatusTag> : total ? <span>Checked by the app</span> : ""}</td>
                        <td style={{ ...td, color: isStale(d) && d.status === "enrolled" ? theme.color.warningText : td.color }}>{d.lastSeenAt ? <>{fmtDateTime(d.lastSeenAt)}<div style={text.hint}>{relativeDue(d.lastSeenAt)}</div></> : "Never"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            {removed.length > 0 && (
              <div style={{ padding: "10px 18px", borderTop: "1px solid var(--c-border)" }}>
                <button type="button" style={{ ...secondaryBtn, fontSize: 12 }} onClick={() => setShowRemoved((v) => !v)}>
                  {showRemoved ? "Hide" : "Show"} {removed.length} removed device{removed.length === 1 ? "" : "s"}
                </button>
              </div>
            )}
          </div>

          {canManage && <ConnectionPanel view={data} isAdmin={isAdmin} onChanged={load} />}
        </div>
      )}

      {adding && data && <AddDeviceDialog onClose={() => { setAdding(false); void load(); }} />}
    </div>
  );
}

function AddDeviceDialog({ onClose }: { onClose: () => void }) {
  const [code, setCode] = useState<{ code: string; expiresAt: string; usesLeft: number } | null>(null);
  const [uses, setUses] = useState(5);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const make = async () => {
    setBusy(true); setError("");
    try { setCode(await api.post("/devices/enrolment-codes", { uses })); }
    catch (e) { setError(apiErrorMessage(e, "Could not make a code.")); }
    finally { setBusy(false); }
  };
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title="Add a device" onClose={onClose} />
      <div style={{ padding: 18, display: "grid", gap: 14 }}>
        {!code ? (
          <>
            <p style={text.body}>You get a code to type into the WingGuard app on each PC. It works for seven days and can be used a set number of times. It is shown once.</p>
            <Field label="How many PCs will use this code">
              <input type="number" min={1} max={100} value={uses} onChange={(e) => setUses(Math.max(1, Math.min(100, Number(e.target.value) || 1)))} style={{ ...input, width: 120 }} />
            </Field>
            {error && <div style={errorText}>{error}</div>}
            <div><button type="button" style={btnPrimary} disabled={busy} onClick={() => void make()}>{busy ? "Making the code..." : "Make a code"}</button></div>
          </>
        ) : (
          <>
            <p style={text.body}>On each PC, open WingGuard, choose Connect to Abacas and enter this code. Keep it private: anyone with it can add a PC to your organisation until it runs out.</p>
            <div style={{ ...text.ref, fontSize: 22, letterSpacing: 2, padding: "14px 16px", border: "1px dashed var(--c-borderStrong)", textAlign: "center" }}>{code.code}</div>
            <div style={text.hint}>Works for {code.usesLeft} PC{code.usesLeft === 1 ? "" : "s"} until {fmtDateTime(code.expiresAt)}.</div>
            <div style={{ display: "flex", gap: 8 }}>
              <button type="button" style={{ ...secondaryBtn, display: "flex", alignItems: "center", gap: 6 }} onClick={() => void navigator.clipboard?.writeText(code.code)}><Copy size={13} /> Copy</button>
              <button type="button" style={btnPrimary} onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </ModalOverlay>
  );
}

function ConnectionPanel({ view, isAdmin, onChanged }: { view: View; isAdmin: boolean; onChanged: () => Promise<void> }) {
  const [escrows, setEscrows] = useState<Escrow[] | null>(null);
  const [opening, setOpening] = useState<Escrow | null>(null);
  const [keyDialog, setKeyDialog] = useState(false);
  const [showKeys, setShowKeys] = useState(false);

  const loadEscrows = useCallback(async () => {
    if (!isAdmin) return;
    try { setEscrows((await api.get<{ escrows: Escrow[] }>("/devices/escrows")).escrows); } catch { setEscrows([]); }
  }, [isAdmin]);
  useEffect(() => { void loadEscrows(); }, [loadEscrows]);

  const revoke = async (id: string) => { await api.del(`/devices/enrolment-codes/${id}`); await onChanged(); };

  return (
    <SheetCard label="Connection and support access">
      <div style={{ display: "grid", gap: 16 }}>
        <div>
          <div style={text.itemTitle}>Codes still in use</div>
          {view.codes.length === 0 ? <div style={text.hint}>None. Choose Add a device to make one.</div> : view.codes.map((c) => (
            <div key={c.id} style={{ display: "flex", gap: 12, alignItems: "center", padding: "6px 0" }}>
              <span style={text.hint}>Made {fmtDateTime(c.createdAt)}, {c.usesLeft} use{c.usesLeft === 1 ? "" : "s"} left, ends {fmtDateTime(c.expiresAt)}</span>
              <button type="button" style={{ ...secondaryBtn, fontSize: 12 }} onClick={() => void revoke(c.id)}>Withdraw</button>
            </div>
          ))}
        </div>

        <div>
          <div style={text.itemTitle}>Support access passwords</div>
          <p style={text.hint}>
            A person at a PC can allow a temporary support account. Its password is locked on the PC to a key you hold, so Abacas cannot read it. Without a key set, the PC does not send one.
          </p>
          <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 6 }}>
            <StatusTag tone={view.escrowKeySet ? "met" : "none"}>{view.escrowKeySet ? "Key set" : "No key set"}</StatusTag>
            {view.escrowKeySetAt && <span style={text.hint}>{fmtDateTime(view.escrowKeySetAt)}</span>}
            {isAdmin && <button type="button" style={{ ...secondaryBtn, display: "flex", alignItems: "center", gap: 6 }} onClick={() => setKeyDialog(true)}><KeyRound size={13} /> {view.escrowKeySet ? "Replace the key" : "Set the key"}</button>}
          </div>
          {isAdmin && escrows && escrows.length > 0 && (
            <div style={{ marginTop: 12, display: "grid", gap: 6 }}>
              {escrows.map((e) => (
                <div key={e.id} style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", padding: "6px 0", borderTop: "1px solid var(--c-border)" }}>
                  <span style={{ ...text.body, minWidth: 160 }}>{e.linkedDevice.name}</span>
                  <span style={text.hint}>{e.expired ? "Support access ended" : `Open until ${fmtDateTime(e.expiresUtc)}`}</span>
                  {e.accesses.length > 0 && <span style={text.hint}>Opened {e.accesses.length} time{e.accesses.length === 1 ? "" : "s"}</span>}
                  {!e.expired && <button type="button" style={{ ...secondaryBtn, fontSize: 12 }} onClick={() => setOpening(e)}>Open the password</button>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <button type="button" style={{ ...secondaryBtn, fontSize: 12 }} onClick={() => setShowKeys((v) => !v)}>{showKeys ? "Hide" : "Show"} technical details</button>
          {showKeys && (
            <div style={{ marginTop: 8, display: "grid", gap: 8 }}>
              <div style={text.hint}>Public keys the PCs trust to check instructions from this platform. The app installs them when it connects; they are shown for a manual install only.</div>
              {view.keys.map((k) => (
                <div key={k.keyId} style={{ ...text.hint, wordBreak: "break-all" }}>
                  <b>{k.keyId}</b>{k.retired ? " (retired)" : ""}: Exponent {k.exponent}, Modulus {k.modulus}
                </div>
              ))}
              {view.keys.length === 0 && <div style={text.hint}>A key is made when the first PC connects.</div>}
            </div>
          )}
        </div>
      </div>
      {keyDialog && <EscrowKeyDialog onClose={() => { setKeyDialog(false); void onChanged(); }} />}
      {opening && <OpenEscrowDialog escrow={opening} onClose={() => { setOpening(null); void loadEscrows(); }} />}
    </SheetCard>
  );
}

function EscrowKeyDialog({ onClose }: { onClose: () => void }) {
  const [modulus, setModulus] = useState("");
  const [exponent, setExponent] = useState("AQAB");
  const [error, setError] = useState("");
  const save = async () => {
    setError("");
    try { await api.put("/devices/escrow-key", { modulus: modulus.trim(), exponent: exponent.trim() }); onClose(); }
    catch (e) { setError(apiErrorMessage(e, "Could not save the key.")); }
  };
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title="Set the support password key" onClose={onClose} />
      <div style={{ padding: 18, display: "grid", gap: 12 }}>
        <p style={text.body}>
          Make a key pair on a computer you trust, keep the private half there, and paste only the public half here (its modulus and exponent, as base64). The platform never holds the private half, so it cannot open a support password. See the device link guide for the command that makes one.
        </p>
        <Field label="Modulus (base64)"><textarea style={{ ...textarea, minHeight: 90 }} value={modulus} onChange={(e) => setModulus(e.target.value)} /></Field>
        <Field label="Exponent (base64)"><input style={input} value={exponent} onChange={(e) => setExponent(e.target.value)} /></Field>
        <p style={text.hint}>Passwords already held stay locked to the previous key.</p>
        {error && <div style={errorText}>{error}</div>}
        <div><button type="button" style={btnPrimary} disabled={!modulus.trim()} onClick={() => void save()}>Save the key</button></div>
      </div>
    </ModalOverlay>
  );
}

function OpenEscrowDialog({ escrow, onClose }: { escrow: Escrow; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const [out, setOut] = useState<{ cipher: string } | null>(null);
  const [error, setError] = useState("");
  const open = async () => {
    setError("");
    try { setOut(await api.post(`/devices/escrows/${escrow.id}/open`, { reason })); }
    catch (e) { setError(apiErrorMessage(e, "Could not open it.")); }
  };
  return (
    <ModalOverlay onClose={onClose}>
      <ModalHeader title={`Support password for ${escrow.linkedDevice.name}`} onClose={onClose} />
      <div style={{ padding: 18, display: "grid", gap: 12 }}>
        {!out ? (
          <>
            <p style={text.body}>Opening this is recorded with your name and reason in the activity log. What you get is still locked: decrypt it with your private key, away from this platform.</p>
            <Field label="Why do you need it"><textarea style={textarea} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500} /></Field>
            {error && <div style={errorText}>{error}</div>}
            <div><button type="button" style={btnPrimary} disabled={reason.trim().length < 10} onClick={() => void open()}>Open</button></div>
          </>
        ) : (
          <>
            <p style={text.body}>Locked password (base64). It works until {fmtDateTime(escrow.expiresUtc)}, when the support account is removed.</p>
            <textarea readOnly style={{ ...textarea, minHeight: 120, fontFamily: "monospace" }} value={out.cipher} onFocus={(e) => e.currentTarget.select()} />
            <div><button type="button" style={{ ...secondaryBtn, display: "flex", alignItems: "center", gap: 6 }} onClick={() => void navigator.clipboard?.writeText(out.cipher)}><Copy size={13} /> Copy</button></div>
          </>
        )}
      </div>
    </ModalOverlay>
  );
}
